import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../store';
import { updateUser } from '../../store/authSlice';
import { apiGet, apiPost } from '../../utils/api';
import { getSocket } from '../../utils/socket';
import { trackGAEvent } from '../../utils/analytics';
import { CheckCircle, ArrowRight, Loader2, AlertTriangle } from 'lucide-react';

export const PaymentSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  
  const [loading, setLoading] = useState(true);
  const [successPlan, setSuccessPlan] = useState<string | null>(null);
  const attemptsRef = useRef(0);
  const hasTrackedPurchaseRef = useRef(false);

  const trackPurchase = (
    sessionId: string,
    plan: string,
    amountTotal?: number,
    currency?: string,
    interval?: string
  ) => {
    if (hasTrackedPurchaseRef.current) return;
    hasTrackedPurchaseRef.current = true;

    let val = amountTotal;
    const curr = currency || 'EUR';
    const itemInterval = interval || 'year';

    if (val === undefined) {
      const priceMap: Record<string, { month: number; year: number }> = {
        starter: { month: 0.99, year: 9.90 },
        pro: { month: 2.49, year: 24.90 },
        expert: { month: 4.99, year: 49.90 },
        ultra: { month: 9.99, year: 99.90 },
      };
      const planPrices = priceMap[plan.toLowerCase()];
      if (planPrices) {
        val = planPrices.year;
      } else {
        val = 0;
      }
    }

    trackGAEvent('purchase', {
      transaction_id: sessionId,
      value: val,
      currency: curr,
      items: [
        {
          item_id: `${plan.toLowerCase()}_${itemInterval}`,
          item_name: plan,
          price: val,
          quantity: 1,
        },
      ],
    });
  };

  // Fallback verification helper
  const verifySessionDirectly = async (sessionId: string) => {
    try {
      console.log('Attempting direct session verification fallback...');
      const response = await apiPost('/api/stripe/verify-checkout', { sessionId });
      if (response && response.plan) {
        console.log('Direct verification succeeded. Upgraded to plan:', response.plan);
        
        trackPurchase(
          sessionId,
          response.plan,
          response.amountTotal,
          response.currency,
          response.interval
        );

        const profile = await apiGet('/api/auth/profile');
        if (profile?.user) {
          dispatch(updateUser(profile.user));
          setSuccessPlan(profile.user.plan);
          setLoading(false);
          return true;
        }
      }
    } catch (err) {
      console.warn('Direct checkout verification fallback failed/pending:', err);
    }
    return false;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // 1. Direct Checkout Verification Fallback
    const sessionId = new URLSearchParams(window.location.search).get('session_id') || 'unknown';
    if (sessionId !== 'unknown' && loading) {
      verifySessionDirectly(sessionId);
    }

    // 2. WebSocket Listener for Real-Time Notification
    const socket = getSocket();
    if (socket && loading) {
      const handleSubscriptionStatus = async (data: { plan: string; status: string }) => {
        console.log('Received real-time subscription update via WebSocket:', data);
        trackPurchase(sessionId, data.plan);
        const profile = await apiGet('/api/auth/profile');
        if (profile?.user) {
          dispatch(updateUser(profile.user));
          setSuccessPlan(profile.user.plan);
          setLoading(false);
        }
      };

      socket.on('subscription_status', handleSubscriptionStatus);
      return () => {
        socket.off('subscription_status', handleSubscriptionStatus);
      };
    }
  }, [loading, dispatch]);

  useEffect(() => {
    let intervalId: any;
    const sessionId = new URLSearchParams(window.location.search).get('session_id') || 'unknown';

    // 3. Resilient Polling Fallback
    const checkPlanStatus = async () => {
      try {
        const data = await apiGet('/api/auth/profile');
        if (data?.user) {
          if (data.user.plan !== 'free' || attemptsRef.current >= 5) {
            if (data.user.plan !== 'free') {
              trackPurchase(sessionId, data.user.plan);
            }
            dispatch(updateUser(data.user));
            setSuccessPlan(data.user.plan);
            setLoading(false);
          } else {
            attemptsRef.current += 1;
          }
        }
      } catch (err) {
        console.error('Error fetching profile on success redirect:', err);
        attemptsRef.current += 1;
        if (attemptsRef.current >= 5) {
          setLoading(false);
        }
      }
    };

    if (loading) {
      intervalId = setInterval(checkPlanStatus, 1500);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [loading, dispatch]);

  const handleReturn = () => {
    navigate('/library');
  };

  const getPlanDisplayName = (p: string | null) => {
    if (!p) return 'Premium';
    return p.charAt(0).toUpperCase() + p.slice(1);
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '20px',
        background: 'var(--bg-base)',
        position: 'relative',
      }}
    >
      <div
        className="glass-panel"
        style={{
          maxWidth: '480px',
          width: '100%',
          padding: '40px 32px',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '24px',
        }}
      >
        {loading ? (
          <>
            <div style={{ position: 'relative', display: 'inline-flex' }}>
              <Loader2 className="spinner" size={64} style={{ color: 'var(--accent-primary)' }} />
            </div>
            <div>
              <h2 style={{ fontSize: '24px', marginBottom: '8px' }}>Confirming Payment...</h2>
              <p style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: '1.5' }}>
                We are finalizing your subscription setup with Stripe. This will only take a moment.
              </p>
            </div>
          </>
        ) : successPlan && successPlan !== 'free' ? (
          <>
            <div
              style={{
                width: '72px',
                height: '72px',
                borderRadius: '50%',
                background: 'rgba(16, 185, 129, 0.1)',
                border: '2px solid var(--status-success)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--status-success)',
                boxShadow: '0 0 20px rgba(16, 185, 129, 0.2)',
              }}
            >
              <CheckCircle size={40} />
            </div>
            
            <div>
              <h1 style={{ fontSize: '28px', fontWeight: 800, marginBottom: '8px', background: 'var(--accent-gradient)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                Subscription Active!
              </h1>
              <p style={{ color: 'var(--text-secondary)', fontSize: '15px', lineHeight: '1.6' }}>
                Thank you! Your payment was successful. Your account has been upgraded to the{' '}
                <strong style={{ color: '#fff' }}>{getPlanDisplayName(successPlan)} Plan</strong>.
              </p>
            </div>

            <div
              style={{
                width: '100%',
                background: 'rgba(255, 255, 255, 0.02)',
                border: '1px solid rgba(255, 255, 255, 0.06)',
                borderRadius: '12px',
                padding: '16px',
                textAlign: 'left',
                fontSize: '13px',
              }}
            >
              <h4 style={{ color: '#fff', fontWeight: 700, marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                New Plan Benefits:
              </h4>
              <ul style={{ paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '6px', color: 'var(--text-secondary)' }}>
                <li>Higher resolution video renders unlocked.</li>
                <li>Increased maximum GPX track length limit.</li>
                <li>Priority rendering queue processing.</li>
                <li>Longer video file cloud retention.</li>
              </ul>
            </div>

            <button
              onClick={handleReturn}
              className="btn btn-primary"
              style={{
                width: '100%',
                padding: '14px 28px',
                borderRadius: '24px',
                fontSize: '15px',
                marginTop: '8px',
              }}
            >
              <span>Go to Track Library</span>
              <ArrowRight size={16} />
            </button>
          </>
        ) : (
          <>
            <div
              style={{
                width: '72px',
                height: '72px',
                borderRadius: '50%',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '2px solid var(--status-error, #ef4444)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--status-error, #ef4444)',
                boxShadow: '0 0 20px rgba(239, 68, 68, 0.2)',
              }}
            >
              <AlertTriangle size={40} />
            </div>
            
            <div>
              <h1 style={{ fontSize: '28px', fontWeight: 800, marginBottom: '8px', color: '#ef4444' }}>
                Confirmation Pending
              </h1>
              <p style={{ color: 'var(--text-secondary)', fontSize: '15px', lineHeight: '1.6' }}>
                We couldn't confirm your subscription update automatically. If your payment was successful, please don't worry—your plan will update shortly.
              </p>
            </div>

            <div
              style={{
                width: '100%',
                background: 'rgba(255, 255, 255, 0.02)',
                border: '1px solid rgba(255, 255, 255, 0.06)',
                borderRadius: '12px',
                padding: '16px',
                textAlign: 'left',
                fontSize: '13px',
              }}
            >
              <p style={{ color: 'var(--text-secondary)', margin: 0, lineHeight: '1.5' }}>
                You can retry synchronization now using the button below, or access the <strong>Settings &gt; Billing</strong> panel in your dashboard later to manually sync.
              </p>
            </div>

            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button
                onClick={async () => {
                  setLoading(true);
                  attemptsRef.current = 0;
                  try {
                    await apiPost('/api/stripe/sync', {});
                    const sessionId = new URLSearchParams(window.location.search).get('session_id');
                    if (sessionId) {
                      await verifySessionDirectly(sessionId);
                    }
                  } catch (e) {
                    console.error('Manual retry sync failed:', e);
                  }
                }}
                className="btn btn-primary"
                style={{
                  width: '100%',
                  padding: '14px 28px',
                  borderRadius: '24px',
                  fontSize: '15px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  marginTop: '8px',
                }}
              >
                <span>Retry Synchronization</span>
              </button>

              <button
                onClick={handleReturn}
                style={{
                  width: '100%',
                  padding: '14px 28px',
                  borderRadius: '24px',
                  fontSize: '15px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  color: '#fff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)'; }}
              >
                <span>Go to Track Library</span>
                <ArrowRight size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
